import { ConvivaContext } from './ConvivaContext'
import { useConfig } from '../index'
import {
    Analytics,
    AdAnalytics,
    Constants as ConvivaConstants,
    ConvivaConstants as ConvivaConstantsType,
    ConvivaMetadata,
    valueof,
    VideoAnalytics,
} from '@convivainc/conviva-js-coresdk'
import { createSignal, onMount } from 'solid-js'
import { isWeb, getDeviceInfo } from '../../utils'
import { profileSelector, useSelector } from '../../store'

interface ConvivaProviderProps {
    children: any
}

interface ConvivaConfig {
    enable: boolean
    customerKey: string
    gatewayUrl: string
    logLevel: number
    applicationName: string
}

const fakeConvivaConfig = {
    enable: false,
    customerKey: 'fake_key',
    gatewayUrl: 'fake_url',
    logLevel: 0,
    applicationName: 'webStv'
}

export const ConvivaProvider = (props: ConvivaProviderProps) => {
    const { getConfig } = useConfig()

    const profile = useSelector(profileSelector)

    const [convivaConfig, setConvivaConfig] = createSignal<ConvivaConfig>(fakeConvivaConfig)
    const [videoAnalytics, setVideoAnalytics] = createSignal<VideoAnalytics>()
    const [adAnalytics, setAdAnalytics] = createSignal<AdAnalytics>()
    const [isSession, setIsSession] = createSignal<Boolean>(false)

    onMount(() => {
        if (!getConfig()?.conviva) return
        setConvivaConfig(getConfig().conviva)
        if (!videoAnalytics()) {
            initializeVideoAnalytics()
            getDefaultVideoAnalytics()
        }
    })

    const initializeVideoAnalytics = () => {
        if (!convivaConfig()?.enable || videoAnalytics()) return
        
        Analytics.init(convivaConfig().customerKey, null, {
            gatewayUrl: convivaConfig()?.gatewayUrl,
            logLevel: convivaConfig()?.logLevel,
        })
        const convivaVideoAnalytics = Analytics.buildVideoAnalytics()
        if(getConfig()?.broadpeak?.adsEnable) {
            var adAnalytics = Analytics.buildAdAnalytics(convivaVideoAnalytics);
            setAdAnalytics(adAnalytics)
        }
        setVideoAnalytics(convivaVideoAnalytics)
    }

    const getDefaultVideoAnalytics = () => {
        const contentInfo = {
            'Conviva.applicationName': convivaConfig()?.applicationName || 'webStv',
            'Conviva.viewerId': profile()?.userId || profile()?.customerId,
            // ['c3.app.version']: appPackage.version
        }

        Analytics.setDeviceMetadata({
            'DeviceType': isWeb ? ConvivaConstants.DeviceType.DESKTOP : ConvivaConstants.DeviceType.SMARTTV,
            'DeviceCategory': isWeb ? ConvivaConstants.DeviceCategory.WEB : ConvivaConstants.DeviceCategory.SMART_TV,
            'DeviceBrand': 'N/A',
            'DeviceManufacturer': 'N/A',
            'DeviceModel': 'N/A',
            'DeviceVersion': 'N/A',
            'OperatingSystemName': getDeviceInfo()?.os?.name,
            'OperatingSystemVersion': 'N/A',
        })
        videoAnalytics()?.setContentInfo(contentInfo)
    }

    const reportPlaybackRequested = (contentInfo?: ConvivaMetadata, initialRequest?: Boolean) => {
        if (convivaConfig()?.enable && videoAnalytics() && (initialRequest || isSession())) {
            setIsSession(true);
            videoAnalytics()?.reportPlaybackRequested(contentInfo)
        }
    }

    const reportPlaybackMetric = (metricKey: any, playerState: string = 'PLAYING') => {
        if (convivaConfig()?.enable && videoAnalytics() && isSession()) {
            videoAnalytics()?.reportPlaybackMetric(metricKey, playerState)
        }
    }

    const reportPlaybackEnded = () => {
        if (convivaConfig()?.enable && videoAnalytics() && isSession()) {
            setIsSession(false)
            videoAnalytics()?.reportPlaybackEnded()
        }
    }

    const reportPlaybackError = (error: string, errorSeverity: number) => {
        if (convivaConfig()?.enable && videoAnalytics() && isSession()) {
            reportPlaybackRequested()
            reportPlaybackMetric(ConvivaConstants.Playback.PLAYER_STATE, 'STOPPED')
            const severity: valueof<ConvivaConstantsType['ErrorSeverity']> =
                errorSeverity === 1 ? ConvivaConstants.ErrorSeverity.FATAL : ConvivaConstants.ErrorSeverity.WARNING
            videoAnalytics()?.reportPlaybackError(error, severity)
        }
    }

    const reportPlaybackFailed = (error: string, contentInfo?: ConvivaMetadata) => {
        if (convivaConfig()?.enable && videoAnalytics() && isSession()) {
            reportPlaybackMetric(ConvivaConstants.Playback.PLAYER_STATE, 'STOPPED')
            videoAnalytics()?.reportPlaybackFailed(error);
        }
    }

    const reportAdStarted = (adInfo: ConvivaMetadata) => {
        if(adAnalytics() && getConfig()?.broadpeak?.adsEnable) {
            adAnalytics()?.reportAdStarted(adInfo)
        }
    }

    const reportAdEnded = () => {
        if(adAnalytics() && getConfig()?.broadpeak?.adsEnable) {
            adAnalytics()?.reportAdEnded()
        }
    }

    const reportAdSkipped = () => {
        if(adAnalytics() && getConfig()?.broadpeak?.adsEnable) {
            adAnalytics()?.reportAdSkipped()
        }
    }

    const reportAdMetric = (metricKey: any, playerState: string = 'PLAYING') => {
        if(adAnalytics() && getConfig()?.broadpeak?.adsEnable) {
            adAnalytics()?.reportAdMetric(metricKey, playerState)
        }
    }

    const setAdInfo = (adInfo: ConvivaMetadata) => {
        if(adAnalytics() && getConfig()?.broadpeak?.adsEnable) {
            adAnalytics()?.setAdInfo(adInfo)
        }
    }

    return (
        <ConvivaContext.Provider
            value={{
                getVideoAnalytics: () => videoAnalytics(),
                reportPlaybackRequested,
                reportPlaybackEnded,
                reportPlaybackMetric,
                reportPlaybackFailed,
                reportPlaybackError,
                reportAdStarted,
                reportAdEnded,
                reportAdSkipped,
                reportAdMetric,
                setAdInfo,
                ConvivaConstants: ConvivaConstants
            }}
        >
            <>{props.children}</>
        </ConvivaContext.Provider>
    )
}

export interface ConvivaProviderBehavior {
    getVideoAnalytics: () => VideoAnalytics | undefined
    reportPlaybackRequested: (contentInfo?: ConvivaMetadata, initialRequest?: Boolean) => void
    reportPlaybackEnded: () => void
    reportPlaybackFailed(errorMessage: string, contentInfo?: ConvivaMetadata): void,
    reportPlaybackMetric: (metricKey: string, playerState: string) => void
    reportPlaybackError: (error: string, errorSeverity: number) => void,
    reportAdStarted: (adInfo: ConvivaMetadata) => void
    reportAdEnded: () => void
    reportAdSkipped: () => void
    reportAdMetric: (metricKey: any, playerState?: string) => void
    setAdInfo: (adInfo: ConvivaMetadata) => void
    ConvivaConstants: ConvivaConstantsType
}
